import type { SchemeEntry, SchemeOptionEntry } from './scheme-reference-loader';

/** What a medical-aid picker hands back: a scheme and option from the list, or text typed when it is not listed. */
export type MedicalAidPick =
  | { scheme: SchemeEntry; option: SchemeOptionEntry }
  | { freeText: { medicalAid: string; medicalAidPlan: string } };

const MAX_RESULTS = 50;

export function filterSchemes(schemes: readonly SchemeEntry[], term: string): SchemeEntry[] {
  const q = term.trim().toLowerCase();
  const hits = q ? schemes.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)) : schemes;
  return hits.slice(0, MAX_RESULTS);
}

/** The option to pre-select: by plan and option code, or by option code alone when no plan code is known. */
export function initialOption(scheme: SchemeEntry, planCode: string | null, optionCode: string | null): SchemeOptionEntry | null {
  if (!optionCode) return null;
  return scheme.options.find((o) => o.optionCode === optionCode && (!planCode || o.planCode === planCode)) ?? null;
}

export function optionLabel(option: SchemeOptionEntry): string {
  return option.label || [option.planName, option.optionName].filter(Boolean).join(' ');
}

/** The plan text a link saves, as the server writes it: the option name is left out when it repeats the plan name. */
export function planText(option: SchemeOptionEntry): string {
  return [option.planName, option.optionName !== option.planName ? option.optionName : null].filter(Boolean).join(' ');
}

/** The patient fields a pick saves, in the shapes Connect's scheme_linked writes back. */
export function medicalAidFields(pick: MedicalAidPick) {
  if ('freeText' in pick) {
    return {
      medicalAid: pick.freeText.medicalAid.trim(),
      medicalAidPlan: pick.freeText.medicalAidPlan.trim(),
      medicalSchemeCode: null,
      medicalPlanCode: null,
      medicalPlanOptionCode: null,
    };
  }
  const { scheme, option } = pick;
  return {
    medicalAid: scheme.name,
    medicalAidPlan: planText(option),
    medicalSchemeCode: { code: scheme.code, name: scheme.name },
    medicalPlanCode: { code: option.planCode, schemeCode: scheme.code, option: option.planName || null },
    medicalPlanOptionCode: { code: option.optionCode, planCode: option.planCode, option: option.optionName || null },
  };
}
